"use client";

import React, { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { questionBankApi, examPaperApi } from "@/lib/api";
import { toast } from "sonner";
import { useTeacherGuard } from "@/lib/hooks/use-auth-guard";
import { 
  BookOpen, 
  FileText, 
  Users,
  TrendingUp,
  PlusCircle,
  Library,
  ClipboardList,
  Clock,
  Wifi,
} from "lucide-react";
import Link from "next/link";
import { teacherNav } from "@/lib/teacher-nav";

export default function TeacherDashboard() {
  const [stats, setStats] = useState({
    questionBanks: 0,
    examPapers: 0,
    totalQuestions: 0,
  });

  // 使用认证守卫
  useTeacherGuard();

  const loadStats = useCallback(async () => {
    try {
      const [banksRes, papersRes] = await Promise.all([
        questionBankApi.list(),
        examPaperApi.list(),
      ]) as [{ data: Array<Record<string, unknown>> }, { data: Array<Record<string, unknown>> }];
      
      const banks = banksRes.data || [];
      const totalQuestions = banks.reduce((acc: number, bank: Record<string, unknown>) => acc + (bank.question_count as number || 0), 0);
      
      setStats({
        questionBanks: banks.length,
        examPapers: (papersRes.data || []).length,
        totalQuestions,
      });
    } catch (error) {
      const err = error as Error;
      toast.error(err.message || "加载失败");
    }
  }, []);

  useEffect(() => {
    // 使用 setTimeout 避免同步 setState
    const timer = setTimeout(() => {
      void loadStats();
    }, 0);
    return () => clearTimeout(timer);
  }, [loadStats]);

  return (
    <DashboardLayout sidebarNav={teacherNav}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">教师中心</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 sm:mt-2">
              管理题库、试卷、考试和学生
            </p>
          </div>
          <div className="mt-2 sm:mt-0">
            <Link href="/teacher/question-banks/new">
              <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base">
                <PlusCircle className="mr-2 h-4 w-4" />
                创建题库
              </Button>
            </Link>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">题库数量</CardTitle>
              <Library className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.questionBanks}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400">已创建的题库</p>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">题目总数</CardTitle>
              <BookOpen className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalQuestions}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400">所有题库题目</p>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">试卷数量</CardTitle>
              <FileText className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.examPapers}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400">已创建的试卷</p>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">活跃学生</CardTitle>
              <Users className="h-4 w-4 text-gray-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">-</div>
              <p className="text-xs text-gray-500 dark:text-gray-400">本月活跃学生</p>
            </CardContent>
          </Card>
        </div>

        {/* 快速操作 */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">快速操作</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/question-banks/new" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <Library className="h-8 w-8 text-blue-600 dark:text-blue-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">创建题库</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    新建一个题库，开始添加题目
                  </p>
                </div>
              </Link>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/papers/new" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <ClipboardList className="h-8 w-8 text-green-600 dark:text-green-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">创建试卷</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    从题库中选择题目，组成试卷
                  </p>
                </div>
              </Link>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/exams" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <FileText className="h-8 w-8 text-purple-600 dark:text-purple-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">查看考试</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    管理考试，查看学生答题情况
                  </p>
                </div>
              </Link>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/users" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <Users className="h-8 w-8 text-orange-600 dark:text-orange-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">学生管理</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    管理学生信息，重置密码等
                  </p>
                </div>
              </Link>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/statistics" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <TrendingUp className="h-8 w-8 text-red-600 dark:text-red-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">数据统计</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    查看考试数据分析和成绩统计
                  </p>
                </div>
              </Link>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/import" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <BookOpen className="h-8 w-8 text-teal-600 dark:text-teal-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">批量导入</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    批量导入题目或学生信息
                  </p>
                </div>
              </Link>
            </Card>

            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer h-full">
              <Link href="/teacher/online-users" className="block h-full p-6">
                <div className="flex flex-col h-full">
                  <Wifi className="h-8 w-8 text-indigo-600 dark:text-indigo-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">在线用户</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    查看和管理当前在线用户
                  </p>
                </div>
              </Link>
            </Card>
          </div>
        </div>

        {/* 最近活动 */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">最近活动</h2>
          <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-dashed rounded-lg">
            <CardContent className="py-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">暂无最近活动</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">您的教学活动记录将在这里显示</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

